import React, { useMemo, useState } from 'react';
import { calculateTeamMetrics } from '../../../shared/metrics/teamMetrics';
import { createMultiTeamMetricCharts } from '../../../shared/charts/metricChartOptions';
import { METRIC_DEFINITIONS } from '../../../shared/constants/metrics';
import ChartModal, { useChartModal } from '../../../shared/components/ChartModal';
import MetricsChartsSection from '../../../shared/components/MetricsChartsSection';
import TeamDetailView from '../../../shared/components/TeamDetailView';
import DashboardHero from './DashboardHero';
import DashboardTeamCard from './DashboardTeamCard';
import '../AdminDashboard.css';

const computeAverages = (teamsData, getTeamMetrics) => {
  if (!teamsData.length) {
    return { overall: 0, compliance: 0, speed: 0, quality: 0, patientCentricity: 0, digitalMaturity: 0 };
  }

  const totals = METRIC_DEFINITIONS.reduce(
    (acc, { key }) => ({ ...acc, [key]: 0 }),
    { overall: 0 }
  );

  teamsData.forEach((team) => {
    const metrics = getTeamMetrics(team);
    METRIC_DEFINITIONS.forEach(({ key }) => {
      totals[key] += metrics[key];
    });
  });

  const count = teamsData.length;
  const averages = {};
  METRIC_DEFINITIONS.forEach(({ key }) => {
    averages[key] = totals[key] / count;
  });
  averages.overall =
    METRIC_DEFINITIONS.reduce((sum, { key }) => sum + averages[key], 0) / METRIC_DEFINITIONS.length;

  return averages;
};

const AdminOverviewSection = ({ teamsData, theme, csvData, onRefresh }) => {
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedTeamForDetail, setSelectedTeamForDetail] = useState(null);
  const [detailViewType, setDetailViewType] = useState('metrics');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    chartModalState,
    openChartModal,
    closeChartModal,
    handleChartModalBackdropClick,
  } = useChartModal();

  const getTeamMetrics = (team) => calculateTeamMetrics(team, csvData, team?.name);

  const charts = useMemo(
    () => createMultiTeamMetricCharts(teamsData, getTeamMetrics, theme === 'dark'),
    [teamsData, csvData, theme]
  );

  const averages = useMemo(
    () => computeAverages(teamsData, getTeamMetrics),
    [teamsData, csvData]
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const handleShowDetail = (team) => {
    setSelectedTeamForDetail(team);
    setDetailViewType('metrics');
    setShowDetailView(true);
  };

  return (
    <div className={`admin-dashboard ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <DashboardHero
        teamsCount={teamsData.length}
        averages={averages}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <section className="dashboard-teams-section">
        <div className="dashboard-section-header">
          <div>
            <h3>Команды</h3>
            <p>Сравнение показателей по каждому направлению</p>
          </div>
          <span className="dashboard-section-count">{teamsData.length} активных</span>
        </div>

        {teamsData.length === 0 ? (
          <div className="dashboard-empty-state">
            <span className="dashboard-empty-icon">📊</span>
            <h4>Нет данных для отображения</h4>
            <p>Добавьте команды в разделе «Команды» или обновите данные</p>
          </div>
        ) : (
          <div className="dashboard-teams-grid">
            {teamsData.map((team, index) => (
              <DashboardTeamCard
                key={team.name}
                team={team}
                metrics={getTeamMetrics(team)}
                index={index}
                onShowDetail={handleShowDetail}
              />
            ))}
          </div>
        )}
      </section>

      {charts.length > 0 && (
        <MetricsChartsSection
          charts={charts}
          chartKeyPrefix={`admin-${teamsData.length}`}
          onChartClick={openChartModal}
          className="dashboard-charts-section"
          title="Аналитика по направлениям"
          subtitle="Нажмите на график для детального просмотра"
        />
      )}

      <ChartModal
        chartModalState={chartModalState}
        onClose={closeChartModal}
        onBackdropClick={handleChartModalBackdropClick}
      />

      {showDetailView && selectedTeamForDetail && (
        <TeamDetailView
          team={selectedTeamForDetail}
          theme={theme}
          detailViewType={detailViewType}
          onDetailViewTypeChange={setDetailViewType}
          onClose={() => {
            setShowDetailView(false);
            setSelectedTeamForDetail(null);
          }}
          getTeamMetrics={getTeamMetrics}
        />
      )}
    </div>
  );
};

export default AdminOverviewSection;
